import { useState, useMemo, useCallback } from 'react';
import { useFormacao, type Formacao } from '@/context/FormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

// ─── Config ───────────────────────────────────────────────────────────────────
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DIAS_SEMANA_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const MODALIDADE_CFG: Record<string, { bar: string; bg: string; text: string; badge: string }> = {
  'Presencial':       { bar: '#10b981', bg: '#ecfdf5', text: '#065f46', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'E-Learning':       { bar: '#0ea5e9', bg: '#f0f9ff', text: '#0c4a6e', badge: 'bg-sky-50 text-sky-700 border-sky-200'            },
  'Misto (Blended)':  { bar: '#8b5cf6', bg: '#f5f3ff', text: '#4c1d95', badge: 'bg-violet-50 text-violet-700 border-violet-200'   },
  'Videoconferência': { bar: '#f59e0b', bg: '#fffbeb', text: '#78350f', badge: 'bg-amber-50 text-amber-700 border-amber-200'       },
};

const ESTADO_CFG: Record<string, { dot: string; label: string; bg: string; text: string }> = {
  'concluida': { dot: '#10b981', label: 'Concluída', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  'planeada':  { dot: '#94a3b8', label: 'Planeada',  bg: 'bg-zinc-100',   text: 'text-zinc-500'    },
  'em_curso':  { dot: '#f59e0b', label: 'Em Curso',  bg: 'bg-amber-50',   text: 'text-amber-700'   },
  'cancelada': { dot: '#ef4444', label: 'Cancelada', bg: 'bg-rose-50',    text: 'text-rose-600'    },
};

function getModalCfg(f: Formacao) {
  return MODALIDADE_CFG[f.modalidade] ?? MODALIDADE_CFG['Presencial'];
}

function getDiasNoMes(ano: number, mes: number) { return new Date(ano, mes + 1, 0).getDate(); }
function getPrimeiroDia(ano: number, mes: number) {
  const d = new Date(ano, mes, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

// ─── CalendarCell ─────────────────────────────────────────────────────────────
interface CalendarCellProps {
  dia: number | null;
  acoes: Formacao[];
  isHoje: boolean;
  isSelected: boolean;
  onClick: () => void;
}

function CalendarCell({ dia, acoes, isHoje, isSelected, onClick }: CalendarCellProps) {
  if (!dia) return <div className="min-h-[90px] rounded-xl" />;
  return (
    <div
      onClick={onClick}
      className={`min-h-[90px] rounded-xl border p-2 transition-all cursor-pointer ${
        isSelected
          ? 'border-zinc-900 bg-zinc-50'
          : isHoje
            ? 'border-zinc-300 bg-white'
            : 'border-zinc-100 bg-white hover:border-zinc-200 hover:bg-slate-50/80'
      }`}
    >
      <div className={`w-6 h-6 flex items-center justify-center rounded-full text-[11px] font-bold mb-1.5 ${
        isHoje ? 'bg-zinc-900 text-white' : 'text-zinc-500'
      }`}>
        {dia}
      </div>
      <div className="space-y-0.5">
        {acoes.slice(0, 2).map((a) => {
          const cfg = getModalCfg(a);
          return (
            <div
              key={a.id}
              className="text-[9px] font-semibold px-1.5 py-0.5 rounded truncate leading-tight"
              style={{ backgroundColor: cfg.bg, color: cfg.text }}
            >
              {a.nome}
            </div>
          );
        })}
        {acoes.length > 2 && (
          <div className="text-[9px] text-zinc-400 px-1">+{acoes.length - 2}</div>
        )}
      </div>
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────
export default function CalendarioFormacaoPage() {
  const { formacoes, loading } = useFormacao();
  const hoje = new Date();
  const [ano, setAno] = useState(2026);
  const [mes, setMes] = useState(0);
  const [diaSel, setDiaSel] = useState<number | null>(null);
  const [acaoDetalhe, setAcaoDetalhe] = useState<Formacao | null>(null);
  const [filtroModalidade, setFiltroModalidade] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');

  // ── Mapa: dia → formações ─────────────────────────────────────────────────
  const diasMap = useMemo(() => {
    const mapa: Record<number, Formacao[]> = {};
    const totalDias = getDiasNoMes(ano, mes);
    for (let d = 1; d <= totalDias; d++) {
      const dataAtual = new Date(ano, mes, d);
      mapa[d] = formacoes.filter((f) => {
        if (!f.data_inicio) return false;
        if (filtroModalidade && f.modalidade !== filtroModalidade) return false;
        if (filtroEstado && estadoNormalizado(f.estado) !== estadoNormalizado(filtroEstado)) return false;
        const inicio = new Date(f.data_inicio); inicio.setHours(0, 0, 0, 0);
        const fim = f.data_fim ? new Date(f.data_fim) : inicio; fim.setHours(23, 59, 59, 999);
        return dataAtual >= inicio && dataAtual <= fim;
      });
    }
    return mapa;
  }, [formacoes, ano, mes, filtroModalidade, filtroEstado]);

  // ── Ações do mês ──────────────────────────────────────────────────────────
  const acoesDoMes = useMemo(() => {
    return formacoes.filter((f) => {
      if (!f.data_inicio) return false;
      if (filtroModalidade && f.modalidade !== filtroModalidade) return false;
      if (filtroEstado && estadoNormalizado(f.estado) !== estadoNormalizado(filtroEstado)) return false;
      const inicio = new Date(f.data_inicio);
      const fim = f.data_fim ? new Date(f.data_fim) : inicio;
      const inicioMes = new Date(ano, mes, 1);
      const fimMes = new Date(ano, mes + 1, 0);
      return inicio <= fimMes && fim >= inicioMes;
    });
  }, [formacoes, ano, mes, filtroModalidade, filtroEstado]);

  // ── Contagem por mês (para mini-calendário lateral) ───────────────────────
  const contagemMensal = useMemo(() => {
    return Array.from({ length: 12 }, (_, m) =>
      formacoes.filter((f) => {
        if (!f.data_inicio) return false;
        const inicio = new Date(f.data_inicio);
        const fim = f.data_fim ? new Date(f.data_fim) : inicio;
        return inicio <= new Date(ano, m + 1, 0) && fim >= new Date(ano, m, 1);
      }).length
    );
  }, [formacoes, ano]);

  // ── Células do calendário ────────────────────────────────────────────────
  const celulas = useMemo((): (number | null)[] => {
    const totalDias = getDiasNoMes(ano, mes);
    const primeiroDia = getPrimeiroDia(ano, mes);
    const arr: (number | null)[] = [...Array(primeiroDia).fill(null), ...Array.from({ length: totalDias }, (_, i) => i + 1)];
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [ano, mes]);

  const acoesDiaSel = diaSel !== null ? (diasMap[diaSel] ?? []) : [];

  const navPrev = useCallback(() => {
    if (mes === 0) { setMes(11); setAno((a) => a - 1); }
    else setMes((m) => m - 1);
    setDiaSel(null);
  }, [mes]);

  const navNext = useCallback(() => {
    if (mes === 11) { setMes(0); setAno((a) => a + 1); }
    else setMes((m) => m + 1);
    setDiaSel(null);
  }, [mes]);

  function isHoje(d: number) {
    return d === hoje.getDate() && mes === hoje.getMonth() && ano === hoje.getFullYear();
  }

  // ── Estatísticas do ano ───────────────────────────────────────────────────
  const statsAno = useMemo(() => {
    const anoFormacoes = formacoes.filter((f) => f.data_inicio && new Date(f.data_inicio).getFullYear() === ano);
    return {
      total: anoFormacoes.length,
      concluidas: anoFormacoes.filter((f) => estadoNormalizado(f.estado) === 'concluida').length,
      planeadas: anoFormacoes.filter((f) => estadoNormalizado(f.estado) === 'planeada').length,
      totalHoras: anoFormacoes.reduce((s, f) => s + (f.duracao_horas ?? 0), 0),
    };
  }, [formacoes, ano]);

  return (
    <main className="flex-1 bg-slate-50/60 overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-calendar-event-line text-white text-lg" />
            </div>
            <div>
              <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Formação</span>
              <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Calendário de Formações</h1>
              <p className="text-[12px] text-zinc-400 mt-0.5">Vista mensal interactiva — {ano}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filtro modalidade */}
            <select
              value={filtroModalidade}
              onChange={(e) => setFiltroModalidade(e.target.value)}
              className="text-[12px] border border-zinc-200 rounded-lg px-3 py-2 bg-white text-zinc-600 focus:outline-none cursor-pointer"
            >
              <option value="">Todas as modalidades</option>
              {Object.keys(MODALIDADE_CFG).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {/* Filtro estado */}
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="text-[12px] border border-zinc-200 rounded-lg px-3 py-2 bg-white text-zinc-600 focus:outline-none cursor-pointer"
            >
              <option value="">Todos os estados</option>
              <option value="concluida">Concluídas</option>
              <option value="planeada">Planeadas</option>
              <option value="em_curso">Em Curso</option>
            </select>
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-5">

        {/* ── KPIs do ano ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: `Ações em ${ano}`,  value: statsAno.total,      icon: 'ri-book-open-line',       accent: 'bg-zinc-100 text-zinc-600'     },
            { label: 'Concluídas',        value: statsAno.concluidas, icon: 'ri-checkbox-circle-line', accent: 'bg-emerald-50 text-emerald-600' },
            { label: 'Planeadas',         value: statsAno.planeadas,  icon: 'ri-calendar-todo-line',   accent: 'bg-zinc-100 text-zinc-500'     },
            { label: 'Total de Horas',    value: `${statsAno.totalHoras}h`, icon: 'ri-time-line',      accent: 'bg-zinc-100 text-zinc-600'     },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.accent}`}>
                <i className={`${s.icon} text-base`} />
              </div>
              <div>
                <p className="text-[20px] font-bold text-zinc-900 leading-tight tabular-nums">{s.value}</p>
                <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Navegação mensal ── */}
        <div className="bg-white rounded-xl border border-zinc-100 p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <button onClick={() => setAno((a) => a - 1)} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line text-sm" />
              </button>
              <span className="text-[14px] font-bold text-zinc-800 px-1 tabular-nums">{ano}</span>
              <button onClick={() => setAno((a) => a + 1)} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line text-sm" />
              </button>
            </div>
            <button
              onClick={() => { setAno(hoje.getFullYear()); setMes(hoje.getMonth()); setDiaSel(null); }}
              className="whitespace-nowrap text-[12px] font-semibold text-zinc-500 bg-zinc-100 hover:bg-zinc-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
            >
              Hoje
            </button>
          </div>
          <div className="grid grid-cols-6 lg:grid-cols-12 gap-1.5">
            {MESES.map((m, idx) => {
              const count = contagemMensal[idx];
              const isActive = mes === idx;
              return (
                <button
                  key={m}
                  onClick={() => { setMes(idx); setDiaSel(null); }}
                  className={`whitespace-nowrap flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl text-center transition-all cursor-pointer ${
                    isActive ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-50 text-zinc-600'
                  }`}
                >
                  <span className={`text-[11px] font-bold`}>{m.slice(0, 3)}</span>
                  {count > 0
                    ? <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-zinc-900/10 text-zinc-700'}`}>{count}</span>
                    : <span className="text-[10px] opacity-0">·</span>
                  }
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Calendário + Painel lateral ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">

          {/* Calendário mensal */}
          <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
            {/* Nav mês */}
            <div className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button onClick={navPrev} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
                  <i className="ri-arrow-left-s-line" />
                </button>
                <h3 className="text-[15px] font-bold text-zinc-800 min-w-[160px] text-center">{MESES[mes]} {ano}</h3>
                <button onClick={navNext} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Legenda */}
                {Object.entries(MODALIDADE_CFG).map(([m, c]) => (
                  <div key={m} className="hidden lg:flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: c.bar }} />
                    <span className="text-[10px] text-zinc-400">{m}</span>
                  </div>
                ))}
                {loading && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line text-sm animate-spin text-zinc-300" />
                  </div>
                )}
              </div>
            </div>

            {/* Dias semana */}
            <div className="px-4 pt-3 grid grid-cols-7 gap-1.5">
              {DIAS_SEMANA_SHORT.map((d) => (
                <div key={d} className="text-center text-[10px] font-semibold text-zinc-400 pb-1.5">{d}</div>
              ))}
            </div>

            {/* Grid */}
            <div className="px-4 pb-4 grid grid-cols-7 gap-1.5">
              {celulas.map((dia, i) => (
                <CalendarCell
                  key={i}
                  dia={dia}
                  acoes={dia !== null ? (diasMap[dia] ?? []) : []}
                  isHoje={dia !== null && isHoje(dia)}
                  isSelected={dia !== null && diaSel === dia}
                  onClick={() => dia !== null && setDiaSel(diaSel === dia ? null : dia)}
                />
              ))}
            </div>

            {/* Linha de info do mês */}
            <div className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between text-[12px]">
              <span className="text-zinc-400">
                <span className="font-semibold text-zinc-700">{acoesDoMes.length}</span> ação(ões) em {MESES[mes]}
              </span>
              {diaSel !== null && (
                <button
                  onClick={() => setDiaSel(null)}
                  className="whitespace-nowrap text-[11px] font-semibold text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
                >
                  Limpar seleção ×
                </button>
              )}
            </div>
          </div>

          {/* Painel lateral */}
          <div className="space-y-4">

            {/* Vista anual compacta */}
            <div className="bg-white rounded-xl border border-zinc-100 p-4">
              <p className="text-[12px] font-bold text-zinc-700 mb-3">Distribuição Anual {ano}</p>
              <div className="space-y-1">
                {MESES.map((m, idx) => {
                  const count = contagemMensal[idx];
                  const maxCount = Math.max(...contagemMensal, 1);
                  const w = Math.round((count / maxCount) * 100);
                  return (
                    <div
                      key={m}
                      className="flex items-center gap-2 cursor-pointer group"
                      onClick={() => { setMes(idx); setDiaSel(null); }}
                    >
                      <span className={`text-[10px] font-semibold w-7 flex-shrink-0 text-right ${mes === idx ? 'text-zinc-900' : 'text-zinc-400'}`}>
                        {m.slice(0, 3)}
                      </span>
                      <div className="flex-1 h-4 bg-zinc-50 rounded overflow-hidden">
                        {count > 0 && (
                          <div
                            className="h-full rounded flex items-center px-1.5 group-hover:opacity-80 transition-all"
                            style={{ width: `${Math.max(w, 8)}%`, backgroundColor: mes === idx ? '#18181b' : '#e4e4e7' }}
                          />
                        )}
                      </div>
                      <span className={`text-[10px] font-bold w-4 flex-shrink-0 tabular-nums ${count > 0 ? 'text-zinc-700' : 'text-zinc-300'}`}>
                        {count || '·'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ações do dia ou do mês */}
            <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100">
                <p className="text-[12px] font-bold text-zinc-700">
                  {diaSel !== null ? `${diaSel} de ${MESES[mes]} ${ano}` : `${MESES[mes]} ${ano}`}
                </p>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  {(diaSel !== null ? acoesDiaSel : acoesDoMes).length} ação(ões)
                </p>
              </div>
              <div className="divide-y divide-zinc-50 max-h-[480px] overflow-y-auto">
                {(diaSel !== null ? acoesDiaSel : acoesDoMes).length === 0 ? (
                  <div className="py-10 flex flex-col items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-calendar-line text-2xl text-zinc-200" />
                    </div>
                    <p className="text-[12px] text-zinc-400">
                      {diaSel !== null ? 'Sem ações neste dia' : 'Sem ações neste mês'}
                    </p>
                  </div>
                ) : (
                  (diaSel !== null ? acoesDiaSel : acoesDoMes).map((f) => {
                    const cfg = getModalCfg(f);
                    const ec = ESTADO_CFG[estadoNormalizado(f.estado)] ?? ESTADO_CFG['planeada'];
                    const isOpen = acaoDetalhe?.id === f.id;
                    return (
                      <div
                        key={f.id}
                        className="px-4 py-3 hover:bg-zinc-50/60 transition-colors cursor-pointer"
                        onClick={() => setAcaoDetalhe(isOpen ? null : f)}
                      >
                        <div className="flex items-start gap-2.5">
                          <div
                            className="w-1 h-full min-h-[32px] rounded-full flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: cfg.bar }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-zinc-800 leading-tight">{f.nome}</p>
                            <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{f.entidade_formadora ?? '—'}</p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {f.data_inicio && (
                                <span className="text-[10px] text-zinc-400">
                                  {new Date(f.data_inicio).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}
                                  {f.data_fim && f.data_fim !== f.data_inicio && ` → ${new Date(f.data_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}`}
                                </span>
                              )}
                              <span
                                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${ec.bg} ${ec.text}`}
                              >
                                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: ec.dot }} />
                                {ec.label}
                              </span>
                              <span className="text-[10px] text-zinc-400">{f.duracao_horas}h</span>
                            </div>
                          </div>
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-zinc-300">
                            {isOpen ? <i className="ri-arrow-up-s-line text-sm" /> : <i className="ri-arrow-down-s-line text-sm" />}
                          </div>
                        </div>

                        {isOpen && (
                          <div className="mt-3 ml-3.5 pt-2.5 border-t border-zinc-100 space-y-1.5">
                            {f.modalidade && (
                              <div className="flex items-center gap-2">
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.badge}`}>{f.modalidade}</span>
                              </div>
                            )}
                            {f.local && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-map-pin-line text-[10px] text-zinc-400" />
                                </div>
                                <span className="text-[11px] text-zinc-500">{f.local}</span>
                              </div>
                            )}
                            {(f.total_participantes ?? 0) > 0 && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-group-line text-[10px] text-zinc-400" />
                                </div>
                                <span className="text-[11px] text-zinc-500">{f.total_participantes} participante(s)</span>
                              </div>
                            )}
                            {f.custo > 0 && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 flex items-center justify-center">
                                  <i className="ri-money-euro-circle-line text-[10px] text-zinc-400" />
                                </div>
                                <span className="text-[11px] text-zinc-500">{Number(f.custo).toLocaleString('pt-PT')} €</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}